import { useEffect, useRef } from 'react';
import {
  Avatar,
  Card,
  Grid,
  Stack,
  TextField,
  FormControlLabel,
  Button,
  Checkbox,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '../../../store';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToppingsCategory as IToppingsCategory } from '../../../store/toppingsCategory/api/interface';
import { FormikProvider, useFormik } from 'formik';
import {
  createToppingsCategory,
  getToppingsCategory,
  patchToppingsCategory,
} from '../../../store/toppingsCategory/api';
import { setStatus } from '../../../store/toppingsCategory/toppingsCategorySlice';

import { decryptString, getAssetURl } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { omit } from 'lodash';
import {
  setStatus as selectToppingsCategorySetStatus,
  reset as resetSelectToppingsCategoryReset,
} from '../../../store/selectToppingCategory/selectToppingCategorySlice';
import toppingsCategorySchema from '../../../validation/toppingsCategory';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import { fillToppingsCategoryState } from '../../../utils/fillToppingsCategoryData';
import { DocumentType } from '../../../enum';
import { uploadAssets } from '../../../utils/uploadAssets';

const ToppingsCategoryForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: any = useParams();
  const location = useLocation();
  const state = location.state as IToppingsCategory;
  const imgInputRef = useRef<HTMLInputElement | null>(null);

  const isAddMode = !params?.id;
  const { isLoading } = useAppSelector((state) => state.toppingsCategory);
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const formik = useFormik({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      imgUrl: '',
      position: '',
      activeState: true,
    },
    validationSchema: toppingsCategorySchema,
    onSubmit: async (values) => {
      try {
        if (isAddMode) {
          const reqBody = omit(values, ['id']);
          await dispatch(createToppingsCategory(reqBody)).unwrap();
        }
        if (!isAddMode) {
          const reqBody: any = values;
          delete reqBody.createdAt;
          delete reqBody.createdBy;
          delete reqBody.updatedAt;
          delete reqBody.updatedBy;
          await dispatch(patchToppingsCategory(reqBody)).unwrap();
        }
        formik.resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectToppingsCategoryReset());
        dispatch(selectToppingsCategorySetStatus({ status: 'idle' }));
        navigation('/toppings-category');
      } catch (error: any) {}
    },
  });

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        DocumentType.ToppingCategory,
        accessToken
      );
      if (uploadResult?.data?.id) {
        formik.setFieldValue('imgUrl', uploadResult?.data?.keypath);
      }
    }
  };

  useEffect(() => {
    const fetchById = async () => {
      if (isAddMode) return;

      if (state) {
        formik.setValues(fillToppingsCategoryState(state));
      } else {
        try {
          const decodeParam = decryptString(params.id);
          const record = await dispatch(
            getToppingsCategory({ id: decodeParam })
          ).unwrap();
          formik.setValues(fillToppingsCategoryState(record));
        } catch (error: any) {
          navigation('/toppings-category');
        }
      }
    };
    fetchById();
  }, [dispatch, isAddMode, navigation, params?.id, formik.setValues, state]);

  return (
    <Grid
      container
      spacing={3}
    >
      <Grid
        item
        xs={12}
        md={12}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <Card sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)',
                      sm: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <TextField
                    fullWidth
                    label={'Label English'}
                    name='labelEn'
                    id='labelEn'
                    onChange={formik.handleChange}
                    value={formik.values.labelEn}
                    error={Boolean(
                      formik.errors.labelEn && formik.touched.labelEn
                    )}
                    helperText={
                      Boolean(
                        formik.errors.labelEn && formik.touched.labelEn
                      ) && formik.errors.labelEn
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Label Arabic'}
                    name='labelAr'
                    id='labelAr'
                    onChange={formik.handleChange}
                    value={formik.values.labelAr}
                    error={Boolean(
                      formik.errors.labelAr && formik.touched.labelAr
                    )}
                    helperText={
                      Boolean(
                        formik.errors.labelAr && formik.touched.labelAr
                      ) && formik.errors.labelAr
                    }
                  />

                  <TextField
                    fullWidth
                    label={'Position'}
                    name='position'
                    id='position'
                    onChange={formik.handleChange}
                    InputProps={{
                      inputComponent: NumericFormatCustom as any,
                    }}
                    value={formik.values.position}
                    error={Boolean(
                      formik.errors.position && formik.touched.position
                    )}
                    helperText={
                      Boolean(
                        formik.errors.position && formik.touched.position
                      ) && formik.errors?.position
                    }
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start',
                    }}
                  >
                    <TextField
                      type='file'
                      fullWidth
                      name='imgUrl'
                      id='imgUrl'
                      inputRef={imgInputRef}
                      onChange={handleFileChange}
                      inputProps={{ multiple: false, accept: 'image/*' }}
                      InputLabelProps={{ shrink: true }}
                      variant='outlined'
                      label={'Image'}
                    />
                    {formik.values?.imgUrl ? (
                      <Box
                        sx={{
                          position: 'relative',
                          width: { xs: '75px', sm: '100px' },
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      >
                        <IconButton
                          size='small'
                          aria-label='remove image'
                          onClick={() => {
                            formik.setFieldValue('imgUrl', '');
                            if (imgInputRef.current) {
                              imgInputRef.current.value = '';
                            }
                          }}
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': {
                              bgcolor: 'grey.100',
                            },
                          }}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                        <Avatar
                          imgProps={{ crossOrigin: 'anonymous' }}
                          src={getAssetURl(formik.values.imgUrl, assetUrl)}
                          sx={{
                            borderRadius: 'unset',
                            objectFit: 'contain',
                            '& img': { objectFit: 'contain' },
                            width: '100%',
                            height: 50,
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                <Grid
                  container
                  spacing={2}
                  py={2}
                >
                  <Grid
                    item
                    xs={12}
                    sm={4}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          name='activeState'
                          onChange={(e) =>
                            formik.setFieldValue(
                              'activeState',
                              e.target.checked
                            )
                          }
                          checked={formik.values.activeState}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 70 } }}
                        />
                      }
                      label={'Active Status'}
                    />
                  </Grid>
                </Grid>

                <Stack
                  alignItems='center'
                  sx={{ mt: 3 }}
                  direction='row'
                  justifyContent='flex-end'
                  gap={1}
                >
                  <Button
                    variant='outlined'
                    onClick={() => navigation('/toppings-category')}
                  >
                    {'Cancel'}
                  </Button>

                  <LoadingButton
                    type='submit'
                    variant='contained'
                    loading={formik.isSubmitting}
                  >
                    {isAddMode
                      ? 'Add Toppings Category'
                      : 'Edit Toppings Category'}
                  </LoadingButton>
                </Stack>
              </Card>
            </form>
          </FormikProvider>
        )}
      </Grid>
    </Grid>
  );
};

export default ToppingsCategoryForm;
